import { Checkbox } from './pilot-ui/checkbox';
import React from 'react';
import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { ChartData, ChartSeries } from '../types';
import NoteMarkerOverlay, { SeriesNotes } from './NoteMarkerOverlay';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { AXIS_TITLE_STYLE, inH2OTickFormatter, referenceLabel } from '../utils/pressureAxis';
import { DATA_LINE_Z_INDEX, REFERENCE_LINE_Z_INDEX, TREND_LINE_Z_INDEX } from './referenceLineLayer';

interface PressureChartProps {
  jsonData: ChartData;
  /** Optional overlay series. When provided with 2+ entries, renders one
   *  colored pressure + trend line per device on shared axes. */
  overlaySeries?: ChartSeries[];
  noteSeries?: SeriesNotes[];
  onNoteImageClick?: (noteId: number) => void;
}

interface TrendInfo {
  lastPressureIndex: number;
  lastPressureValue: number;
}

export interface TargetGroup {
  value: number;
  labels: string[];
}

/**
 * FS-443 made the notice/change targets per-device (filter_eol_pa /
 * filter_notify_pa), so an overlay of devices with different thresholds can no
 * longer borrow the first series' pair.
 *
 * The two reference lines are grouped INDEPENDENTLY, not as a (notice, change)
 * pair. Grouping by the pair emits one line per pair, so two devices sharing a
 * notice but differing on change would draw two identical orange lines at the
 * same y — overlapping strokes and a duplicate React key, since each line is
 * keyed by its own pressure. Grouping has to match keying.
 */
export const groupTargets = (
  series: ChartSeries[],
  pick: (t: { notice: number; change: number }) => number,
): TargetGroup[] =>
  series.reduce<TargetGroup[]>((groups, s) => {
    const value = pick(s.data.targets);
    const existing = groups.find((g) => g.value === value);
    if (existing) existing.labels.push(s.label);
    else groups.push({ value, labels: [s.label] });
    return groups;
  }, []);

/**
 * Qualify a reference line with its devices only when that line's own value is
 * not shared by every series — an unambiguous line needs no qualifier.
 */
export const targetLabel = (base: string, groups: TargetGroup[], labels: string[]): string =>
  groups.length > 1 ? `${base} — ${labels.join(', ')}` : base;

/** Exact axis floor: whole-Pa snug bound under the lowest point, so recharts
 *  never nice-rounds a barely-negative off band down a full tick step. */
export const snugFloor = (dataMin: number): number => Math.floor(dataMin);

/** Same, but never above zero — the include-zero checkbox's floor. */
export const snugZeroFloor = (dataMin: number): number => Math.min(0, Math.floor(dataMin));

/** Exact axis ceiling: whole-Pa snug bound over the highest point. Both domain
 *  ends must be exact — recharts expands the WHOLE domain to tick multiples
 *  when either end is 'auto', which drags the floor back down (-1 -> -9). */
export const snugCeil = (dataMax: number): number => Math.ceil(dataMax);

/** Same, but never below zero — the include-zero checkbox's ceiling. */
export const snugZeroCeil = (dataMax: number): number => Math.max(0, Math.ceil(dataMax));

/** Lowest and highest number the matching keys contribute across the chart
 *  rows. Infinite bounds mean those keys carried no numbers at all. */
export const measureRange = (
  rows: Record<string, string | number | null>[],
  keyPattern: RegExp,
): [number, number] => {
  let min = Infinity;
  let max = -Infinity;
  rows.forEach((row) =>
    Object.entries(row).forEach(([key, value]) => {
      if (typeof value !== 'number' || !keyPattern.test(key)) return;
      if (value < min) min = value;
      if (value > max) max = value;
    }),
  );
  return [min, max];
};

/**
 * The expanded view's domain: snug to the pressure band, stretched to keep
 * every rendered target line in frame, and never collapsed onto a single row —
 * a perfectly flat whole-Pa band would otherwise hand recharts a zero-height
 * domain.
 *
 * Target lines default to ifOverflow="discard", so a change target below an
 * overdue filter's band (band 160-200 Pa, target 150) would silently vanish
 * from the expanded view. Stretching to reach it costs some zoom on a device
 * far from its threshold; a disappearing threshold line costs more.
 *
 * A target at or below zero is the one that cannot be honoured: it would drag
 * the expanded floor onto zero, which is precisely where the include-zero
 * floor sits, and the two checkbox states would compute the same pair again —
 * FS-587. The include-zero floor is `min(0, ...)` and so always <= 0, so
 * admitting only targets whose whole-Pa floor is >= 1 keeps the two states
 * provably distinct whenever the band itself clears zero.
 */
export const bandDomain = (
  min: number,
  max: number,
  targets: number[] = [],
): [number, number] => {
  const inFrame = targets.filter((t) => snugFloor(t) > 0);
  const floor = Math.min(snugFloor(min), ...inFrame.map(snugFloor));
  return [floor, Math.max(snugCeil(max), ...inFrame.map(snugCeil), floor + 1)];
};

// Every dataKey drawn as a Line on the left axis. `cal_` is deliberately
// absent: the dynamic calibration is tooltip-only, and its near-zero values
// must not drag the axis floor down.
const PLOTTED_KEY = /^(pressure|trend|mode|ghost|off|zero)_/;

// The pressure band alone — the plotted keys minus the FS-493 fan-off level.
// FS-587: a corrected off level is ~0 Pa by definition, so measuring the
// unchecked domain over PLOTTED_KEY put its floor at or below zero on every
// device that has fan-off data. Both checkbox states then computed the same
// pair, the axis was permanently zero-based and the expanded pressure-drop
// view was unreachable. The expanded view is measured over the band; the
// include-zero view still spans everything drawn.
const BAND_KEY = /^(pressure|trend|mode|ghost)_/;

// The fan-off level on its own, used only to say whether its markers fell
// below the current view.
const OFF_KEY = /^off_/;

// Plan 81: max-speed estimates that are NOT a direct observation — rendered as
// hollow points so a smooth multi-mode line isn't mistaken for measured data.
const IMPUTED_OBSERVATIONS = new Set([
  'imputed_from_band', 'provisional_single_mode', 'carried_forward',
  'below_threshold', 'none',
]);

const isImputedPressure = (observation: unknown): boolean =>
  typeof observation === 'string' && IMPUTED_OBSERVATIONS.has(observation);

const pressureDotAppearance = (imputed: boolean, color: string) => ({
  r: imputed ? 3 : 2.4,
  fill: imputed ? '#ffffff' : color,
  stroke: color,
  strokeWidth: imputed ? 1.2 : 1,
  fillOpacity: imputed ? 0.85 : 1,
});

// Custom recharts dot: solid for observed/standard, hollow for imputed.
const makePressureDot = (seriesId: string | number, color: string) =>
  // eslint-disable-next-line react/display-name
  (props: any) => {
    const { cx, cy, payload, index } = props;
    const key = `dot-${seriesId}-${index}`;
    if (cx == null || cy == null || payload?.[`pressure_${seriesId}`] == null) {
      return <circle key={key} r={0} />;  // no marker for gaps
    }
    const obs = payload?.[`obs_${seriesId}`];
    return (
      <circle
        key={key}
        cx={cx}
        cy={cy}
        {...pressureDotAppearance(isImputedPressure(obs), color)}
      />
    );
  };

// Plan 81 ghost bands: a mode the tracker still carries but did not see that
// day, drawn as a dashed ring at its predicted position. Observed non-max
// bands are small faded solid dots so the actual level ladder stays visible.
// FS-1052: on a day the max speed was not observed, a measured lower band is
// that day's only real reading, so it is drawn like an observed max-speed
// point instead of faded.
const makeModeDot = (ghost: boolean, seriesId: string | number, color: string) =>
  // eslint-disable-next-line react/display-name
  (props: any) => {
    const { cx, cy, value, index, dataKey, payload } = props;
    const key = `mode-${dataKey}-${index}`;
    if (cx == null || cy == null || value == null) {
      return <circle key={key} r={0} />;
    }
    if (ghost) {
      return (
        <circle
          key={key}
          cx={cx}
          cy={cy}
          r={3.5}
          fill="none"
          stroke={color}
          strokeWidth={1.2}
          strokeDasharray="2 2"
          opacity={0.55}
        />
      );
    }
    if (isImputedPressure(payload?.[`obs_${seriesId}`])) {
      return <circle key={key} cx={cx} cy={cy} {...pressureDotAppearance(false, color)} />;
    }
    return <circle key={key} cx={cx} cy={cy} r={2} fill={color} fillOpacity={0.45} />;
  };

// FS-493: the day's observed fan-off band level, drawn as a small gray marker
// regardless of series color so a run of gray-only days reads as "the HVAC was
// off", not "the device stopped reporting". In overlay mode a faint ring in
// the series color keeps the devices attributable.
const makeOffDot = (ringColor: string | null) =>
  // eslint-disable-next-line react/display-name
  (props: any) => {
    const { cx, cy, value, index, dataKey } = props;
    const key = `off-${dataKey}-${index}`;
    if (cx == null || cy == null || value == null) {
      return <circle key={key} r={0} />;
    }
    return (
      <circle
        key={key}
        cx={cx}
        cy={cy}
        r={2.4}
        fill="#6b7280"
        stroke={ringColor ?? 'none'}
        strokeWidth={ringColor ? 1 : 0}
        strokeOpacity={0.6}
      />
    );
  };

/** Multi-mode analyses say how each day's max-speed value was obtained;
 *  standard analyses carry no observation kinds. */
const hasObservationKinds = (data: ChartData): boolean =>
  (data.observations || []).some((o) => o != null);

// FS-1051: on an analysis with observation kinds the trend line is anchored at
// the last day the chart draws solid — a reading, whether `observed` or the
// envelope analysis's `envelope_observed` — so an estimated max-speed value
// (IMPUTED_OBSERVATIONS) never sets where the line is drawn. Standard analyses
// anchor at the last reading, as before.
const computeTrendInfo = (data: ChartData): TrendInfo => {
  const isAnchor = hasObservationKinds(data)
    ? (i: number) => !isImputedPressure(data.observations?.[i])
    : () => true;
  for (let i = data.pressures.length - 1; i >= 0; i--) {
    if (data.pressures[i] != null && isAnchor(i)) {
      return { lastPressureIndex: i, lastPressureValue: data.pressures[i] };
    }
  }
  return { lastPressureIndex: -1, lastPressureValue: 0 };
};

const computeTrendValue = (
  data: ChartData,
  trend: TrendInfo,
  cutoffDate: Date,
  rowIndex: number,
): number | null => {
  if (trend.lastPressureIndex < 0) return null;
  // FS-579: no trend line at all when there is no rate yet. The slope is 0
  // there, and drawing it lays a flat line through the last reading, which
  // says "this filter has stopped loading" — a claim a filter period with one
  // processed day cannot support.
  if (data.trend_line.rate_unavailable) return null;
  const currentDate = new Date(data.dates[rowIndex]);
  if (currentDate < cutoffDate) return null;
  if (data.pressures[rowIndex] == null) return null;
  const daysFromEnd = trend.lastPressureIndex - rowIndex;
  return trend.lastPressureValue - data.trend_line.slope * daysFromEnd;
};

// Browser-remembered preference: anchor the pressure axis at zero instead of
// letting recharts fit the data range.
const ZERO_PREF_KEY = 'pressureChart.includeZero';

const PressureChart: React.FC<PressureChartProps> = ({
  jsonData,
  overlaySeries,
  noteSeries,
  onNoteImageClick,
}) => {
  const isOverlay = !!overlaySeries && overlaySeries.length > 1;
  const activeDot = useAnalysisActiveDot(true);

  const [includeZero, setIncludeZero] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem(ZERO_PREF_KEY) === '1';
    } catch {
      return false;
    }
  });
  const toggleIncludeZero = () => {
    const next = !includeZero;
    setIncludeZero(next);
    try {
      localStorage.setItem(ZERO_PREF_KEY, next ? '1' : '0');
    } catch {
      // storage unavailable (private mode) — preference just won't persist
    }
  };
  // Normalize inputs into a series array so the rest of the component has one shape.
  const series: ChartSeries[] = isOverlay
    ? overlaySeries!
    : [{ id: 'solo', label: 'Pressure', color: '#2563eb', data: jsonData }];

  if (!series[0]?.data) return null;

  // Build a merged, date-aligned data frame covering all series.
  const allDatesSet = new Set<string>();
  series.forEach((s) => s.data.dates.forEach((d) => allDatesSet.add(d)));
  const allDates = Array.from(allDatesSet).sort();

  const trendInfoPerSeries = series.map((s) => computeTrendInfo(s.data));

  // Plan 81: collect the mode ids each series ever shows, split by whether the
  // mode was observed or is a ghost that day (one recharts line per id+kind).
  const modeKeysPerSeries = series.map((s) => {
    const obsIds = new Set<string>();
    const ghostIds = new Set<string>();
    (s.data.mode_daily || []).forEach((day) =>
      day?.forEach((m) => {
        // Off is rendered from `off_levels` (FS-493), not as a mode band.
        if (m.mode_id === 'off') return;
        if (m.observed === false) ghostIds.add(m.mode_id);
        else obsIds.add(m.mode_id);
      }),
    );
    return {
      obsIds: Array.from(obsIds).sort(),
      ghostIds: Array.from(ghostIds).sort(),
    };
  });
  const hasGhosts = modeKeysPerSeries.some((k) => k.ghostIds.length > 0);

  // FS-493: any day with an observed fan-off band level gets a gray marker.
  const hasOffLevels = series.some((s) =>
    (s.data.off_levels || []).some((v) => v != null),
  );

  // 30-day cutoff is per-series (each device has its own "last" date).
  const cutoffPerSeries = series.map((s) => {
    const lastDate = new Date(s.data.dates[s.data.dates.length - 1]);
    const cutoff = new Date(lastDate);
    cutoff.setDate(cutoff.getDate() - 30);
    return cutoff;
  });

  // For each merged date, build { date, pressure_<id>, trend_<id>, ... }.
  const chartData = allDates.map((date) => {
    const row: Record<string, string | number | null> = { date };
    series.forEach((s, si) => {
      const localIdx = s.data.dates.indexOf(date);
      const pressure = localIdx >= 0 ? s.data.pressures[localIdx] : null;
      row[`pressure_${s.id}`] = pressure ?? null;
      // Plan 81: carry the per-point observation kind so the dot renderer can
      // show observed (solid) vs imputed/carried-forward (hollow).
      row[`obs_${s.id}`] =
        localIdx >= 0 ? (s.data.observations?.[localIdx] ?? null) : null;
      row[`trend_${s.id}`] =
        localIdx >= 0
          ? computeTrendValue(s.data, trendInfoPerSeries[si], cutoffPerSeries[si], localIdx)
          : null;
      row[`cal_${s.id}`] =
        localIdx >= 0 ? (s.data.calibrations?.[localIdx] ?? null) : null;
      row[`off_${s.id}`] =
        localIdx >= 0 ? (s.data.off_levels?.[localIdx] ?? null) : null;
      row[`zero_${s.id}`] =
        localIdx >= 0 ? (s.data.zero_pa?.[localIdx] ?? null) : null;
      const md = localIdx >= 0 ? s.data.mode_daily?.[localIdx] : null;
      md?.forEach((m) => {
        if (m.mode_id === 'off') return;
        if (m.observed === false) {
          row[`ghost_${s.id}_${m.mode_id}`] = m.predicted_corrected_pa ?? null;
        } else {
          row[`mode_${s.id}_${m.mode_id}`] = m.median_corrected_pa ?? null;
        }
      });
    });
    return row;
  });

  // FS-1052: the legend explains a solid lower-band point only when the chart
  // draws one — a measured lower band on a day the max speed was estimated.
  const hasSolidBands = series.some((s, si) =>
    chartData.some(
      (row) =>
        isImputedPressure(row[`obs_${s.id}`]) &&
        modeKeysPerSeries[si].obsIds.some((mid) => row[`mode_${s.id}_${mid}`] != null),
    ),
  );

  // The CSV carries every column the chart draws or shows in its tooltip, in
  // the same units, plus each device's two target lines.
  const prefix = (s: ChartSeries) => (isOverlay ? `${s.label} — ` : '');
  const csvColumns: CsvColumn<(typeof chartData)[number]>[] = [
    { header: 'Date', value: 'date' },
    ...series.flatMap((s, si) => [
      { header: `${prefix(s)}Pressure (Pa)`, value: `pressure_${s.id}` },
      { header: `${prefix(s)}Pressure observation`, value: `obs_${s.id}` },
      { header: `${prefix(s)}Trend 30d (Pa)`, value: `trend_${s.id}` },
      { header: `${prefix(s)}Dynamic calibration (Pa)`, value: `cal_${s.id}` },
      { header: `${prefix(s)}System off level (Pa)`, value: `off_${s.id}` },
      ...(s.data.zero_pa ? [
        { header: `${prefix(s)}Zero (raw off level) (Pa)`, value: `zero_${s.id}` },
      ] : []),
      ...modeKeysPerSeries[si].obsIds.map((mid) => (
        { header: `${prefix(s)}${mid} level (Pa)`, value: `mode_${s.id}_${mid}` }
      )),
      ...modeKeysPerSeries[si].ghostIds.map((mid) => (
        { header: `${prefix(s)}${mid} ghost (Pa)`, value: `ghost_${s.id}_${mid}` }
      )),
      { header: `${prefix(s)}Notice target (Pa)`, value: () => s.data.targets.notice },
      { header: `${prefix(s)}Change target (Pa)`, value: () => s.data.targets.change },
    ]),
  ];

  // Recharts resolves a domain per axis, from the series bound to that axis
  // alone — so a function domain left the inH2O axis, which carries no series
  // of its own, resolving against an empty range and collapsing to a single
  // midpoint tick. One explicit number pair, measured here over exactly the
  // keys the left-axis Lines draw, keeps both axes on the same scale so 0 Pa
  // and 0.00 inH2O land on the same row.
  const [plottedMin, plottedMax] = measureRange(chartData, PLOTTED_KEY);
  const [bandMin, bandMax] = measureRange(chartData, BAND_KEY);
  const [offMin] = measureRange(chartData, OFF_KEY);

  const noticeTargets = groupTargets(series, (t) => t.notice);
  const changeTargets = groupTargets(series, (t) => t.change);
  // Exactly the values the ReferenceLines below are built from, so a target
  // that is never drawn cannot stretch the axis.
  const targetValues = [...noticeTargets, ...changeTargets].map((g) => g.value);

  // Checked: span everything drawn, reaching zero from whichever side the data
  // sits on, so the off markers and the zero line are both in frame. Unchecked:
  // hug the pressure band and leave the fan-off cluster below the floor — that
  // IS the expanded pressure-drop view, and measuring it over the band rather
  // than the whole plotted set is what keeps the two states apart on a device
  // with fan-off data (FS-587). The expanded end also reaches out to the notice
  // and change targets so a threshold line never silently drops out of it. A
  // payload carrying off levels but no band at all falls back to the plotted
  // set.
  // Both bounds are exact whole Pa, never 'auto': any 'auto' end makes recharts
  // expand the whole domain to tick multiples (e.g. -0.7 Pa -> a -9 or -15
  // floor), which drags the floor back down and undoes the snug fit.
  const yDomain: [number, number] = Number.isFinite(plottedMin)
    ? includeZero
      ? [snugZeroFloor(plottedMin), snugZeroCeil(plottedMax)]
      : bandDomain(
          Number.isFinite(bandMin) ? bandMin : plottedMin,
          Number.isFinite(bandMax) ? bandMax : plottedMax,
          targetValues,
        )
    : [0, 1];

  const formatTooltip = (value: ValueType, name: string) => {
    if (value === null || value === undefined) return ['-', name];
    const val = typeof value === 'number' ? value.toFixed(2) : value;
    return [`${val} Pa`, name];
  };

  // Custom tooltip: default series rows plus a "Dyn cal" line read straight
  // from the data row. Rendered this way (not as an invisible <Line>) so the
  // near-zero calibration values don't participate in the y-axis domain.
  const renderTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (!active || !payload || payload.length === 0) return null;
    const row = payload[0].payload as Record<string, string | number | null>;
    return (
      <div className="bg-white border border-border rounded-sm px-3 py-2 text-sm shadow-sm">
        <p className="font-medium mb-1">{label}</p>
        {payload
          .filter((entry: any) => entry.value != null)
          .map((entry: any) => (
            <p key={entry.dataKey} style={{ color: entry.color || '#374151' }}>
              {entry.name}: {formatTooltip(entry.value, entry.name)[0]}
            </p>
          ))}
        {series.map((s) => {
          const cal = row[`cal_${s.id}`];
          if (cal == null) return null;
          return (
            <p key={`cal_${s.id}`} className="text-muted-foreground">
              {isOverlay ? `${s.label} — ` : ''}Dyn cal: {(cal as number).toFixed(2)} Pa
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full mt-4">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="flex justify-end">
            <ChartCsvButton chart="pressure" rows={chartData} columns={csvColumns} />
          </div>
          {/* Fixed height must wrap ONLY the chart: ResponsiveContainer fills
              100% of it, so anything after it inside the same fixed-height box
              would render past the boundary and paint over the next block. */}
          <div style={{ height: '500px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 20, right: 50, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" yAxisId="left" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={70}
              />
              {/* allowDataOverflow is what makes the number pair authoritative:
                  without it recharts widens a numeric domain back out to the
                  series range (Math.min/Math.max against the data), so an
                  expanded domain sitting above the fan-off markers would be
                  pulled straight back down to them. It also clips those
                  markers to the plot area instead of letting them paint over
                  the axis, and it keeps the two axes from drifting apart. */}
              <YAxis
                yAxisId="left"
                domain={yDomain}
                allowDataOverflow
                label={{ value: 'Pressure (Pa)', angle: -90, position: 'insideLeft', style: AXIS_TITLE_STYLE }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={yDomain}
                allowDataOverflow
                tick={{ fontSize: 12 }}
                label={{ value: 'Pressure (inH₂O)', angle: 90, position: 'insideRight', style: AXIS_TITLE_STYLE }}
                tickFormatter={inH2OTickFormatter(yDomain)}
              />
              <Tooltip content={renderTooltip} />
              <Legend
                itemSorter={null}
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{ paddingTop: '20px' }}
              />
              {/* FS-493: anchor for the fan-off level. Default ifOverflow
                  discards the line whenever the domain excludes 0, so it
                  composes with the include-zero checkbox below — check it and
                  the line (and the gap down to zero) appears. */}
              <ReferenceLine
                y={0}
                zIndex={REFERENCE_LINE_Z_INDEX}
                stroke="#9ca3af"
                strokeDasharray="3 3"
                label={referenceLabel('Fan-off (0)', 0, yDomain, 'above')}
                yAxisId="left"
              />
              {noticeTargets.map((g) => (
                <ReferenceLine
                  key={`notice_${g.value}`}
                  zIndex={REFERENCE_LINE_Z_INDEX}
                  y={g.value}
                  stroke="orange"
                  strokeDasharray="3 3"
                  label={referenceLabel(
                    targetLabel('Notice Target', noticeTargets, g.labels),
                    g.value,
                    yDomain,
                    'below',
                  )}
                  yAxisId="left"
                />
              ))}
              {changeTargets.map((g) => (
                <ReferenceLine
                  key={`change_${g.value}`}
                  zIndex={REFERENCE_LINE_Z_INDEX}
                  y={g.value}
                  stroke="red"
                  strokeDasharray="3 3"
                  label={referenceLabel(
                    targetLabel('Change Target', changeTargets, g.labels),
                    g.value,
                    yDomain,
                    'above',
                  )}
                  yAxisId="left"
                />
              ))}
              {series.map((s) => (
                <Line
                  key={`pressure_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`pressure_${s.id}`}
                  stroke={s.color}
                  dot={makePressureDot(s.id, s.color)}
                  activeDot={activeDot}
                  name={isOverlay ? `${s.label} — Pressure` : 'Pressure'}
                  yAxisId="left"
                  strokeWidth={2}
                  connectNulls={true}
                />
              ))}
              {series.map((s) => (
                <Line
                  key={`trend_${s.id}`}
                  zIndex={TREND_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`trend_${s.id}`}
                  stroke={isOverlay ? s.color : '#dc2626'}
                  dot={false}
                  activeDot={activeDot}
                  strokeDasharray="5 5"
                  name={isOverlay ? `${s.label} — Trend (30d)` : 'Trend (30d)'}
                  yAxisId="left"
                  strokeWidth={2}
                  connectNulls={true}
                />
              ))}
              {series.map((s, si) =>
                modeKeysPerSeries[si].obsIds.map((mid) => (
                  <Line
                    key={`mode_${s.id}_${mid}`}
                    zIndex={DATA_LINE_Z_INDEX}
                    dataKey={`mode_${s.id}_${mid}`}
                    stroke="none"
                    dot={makeModeDot(false, s.id, s.color)}
                    activeDot={false}
                    legendType="none"
                    name={isOverlay ? `${s.label} — ${mid} level` : `${mid} level`}
                    yAxisId="left"
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )),
              )}
              {series.map((s, si) =>
                modeKeysPerSeries[si].ghostIds.map((mid) => (
                  <Line
                    key={`ghost_${s.id}_${mid}`}
                    zIndex={DATA_LINE_Z_INDEX}
                    dataKey={`ghost_${s.id}_${mid}`}
                    stroke="none"
                    dot={makeModeDot(true, s.id, s.color)}
                    activeDot={false}
                    legendType="none"
                    name={isOverlay ? `${s.label} — ${mid} (ghost)` : `${mid} (ghost)`}
                    yAxisId="left"
                    isAnimationActive={false}
                    connectNulls={false}
                  />
                )),
              )}
              {series.map((s) => (
                <Line
                  key={`off_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  dataKey={`off_${s.id}`}
                  stroke="none"
                  dot={makeOffDot(isOverlay ? s.color : null)}
                  activeDot={false}
                  legendType="none"
                  name={isOverlay ? `${s.label} — System off level` : 'System off level'}
                  yAxisId="left"
                  isAnimationActive={false}
                  connectNulls={false}
                />
              ))}
              {series.filter((s) => s.data.zero_pa).map((s) => (
                <Line
                  key={`zero_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`zero_${s.id}`}
                  stroke="var(--chart-3)"
                  strokeDasharray="4 2"
                  name={isOverlay ? `${s.label} — Zero (raw off level)` : 'Zero (raw off level)'}
                  yAxisId="left"
                  strokeWidth={2}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
              {noteSeries && noteSeries.length > 0 && onNoteImageClick && (
                <NoteMarkerOverlay seriesNotes={noteSeries} onImageClick={onNoteImageClick} />
              )}
            </LineChart>
          </ResponsiveContainer>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1 select-none cursor-pointer w-fit">
            <Checkbox
              className="size-6"
              checked={includeZero}
              onCheckedChange={toggleIncludeZero}
              title="Include zero on the pressure axis"
            />
            Include zero on the pressure axis
          </label>
          {series.some((s) => hasObservationKinds(s.data)) && (
            <p className="text-xs text-muted-foreground mt-1">
              ○ hollow points = estimated / carried-forward max-speed pressure
              (not a direct measurement that day); ● solid = observed.
              {hasSolidBands && (
                <>
                  {' '}● solid lower points = a lower fan speed that was
                  measured on a day the top speed did not run.
                </>
              )}
              {hasGhosts && (
                <>
                  {' '}Small faded dots = observed fan-speed band levels; dashed
                  rings = ghost bands — modes the tracker still carries but did
                  not see that day, drawn where they are expected to reappear.
                </>
              )}
            </p>
          )}
          {hasOffLevels && (
            <p className="text-xs text-muted-foreground mt-1">
              Gray points = the system&apos;s off (fan-off) level on days it was
              observed — a run of gray-only days means the HVAC was off, not
              that the device stopped reporting.
              {offMin < yDomain[0] &&
                ' They sit below this view, which is fitted to the pressure' +
                  ' band — tick the box above to bring them into frame.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default PressureChart;
